import React, { useState } from "react";
import { StyleSheet, View } from "react-native";
import { useForm, Controller } from "react-hook-form";

import CustomText from "../../../common/general/CustomText/CustomText";
import CustomTextInput from "../../../common/general/CustomTextInput/CustomTextInput";
import CustomSpacing from "../../../common/layout/CustomSpacing/CustomSpacing";
import FormLayout from "../common/FormLayout";
import { DatePickerInput } from "react-native-paper-dates";
import { ActivityState, StoragePathType } from "../../../../graphql/generated/graphql";
import { File, Remove } from "../../../svgs/CustomCoursework";
import CustomButton from "../../../common/general/CustomButton/CustomButton";
import FileUploadComponent from "../../../file/FileUploadComponent/FileUploadComponent";
import { ProcessedFile } from "../../../../hooks/useFileUpload";

const maybeFormatDueDate = (date) => {
  if (!date) return "";
  if (typeof date === "string" && date.endsWith("Z")) {
    return new Date(date);
  }
  return date;
};

const deliverableTypeOptions = [
  "DT-Presentation",
  "DT-Document",
  "DT-Image Set",
  "DT-Link",
  "DT-Prototype",
  "DT-Repo",
  "DT-Other",
];

const ProjectForm = ({ activityData, onSave, onCancel, error, isSubmitting, isNewActivity }) => {
  // Initialize react-hook-form
  const {
    control,
    getValues,
    setValue,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      activityName: activityData?.title || activityData?.name || "",
      description: activityData?.description || "",
      scopeDocument: activityData?.scopeDocument || "",
      deliverableTypes: activityData?.deliverableTypes || [],
      assessmentCriteria: activityData?.assessmentCriteria?.join("\n") || "",
      expectedOutcomes: activityData?.expectedOutcomes?.join("\n") || "",
      dueDate: maybeFormatDueDate(activityData?.dueDate) || "",
      activityState: activityData?.activityState || "draft",
      scheduledDate: activityData?.scheduledDate || undefined,
      files: activityData?.files ?? [],
    },
  });
  const watchActivityName = watch("activityName");
  const watchDescription = watch("description");
  const watchDueDate = watch("dueDate");
  const watchActivityState = watch("activityState");
  const watchFiles = watch("files");

  const [uploadError, setUploadError] = useState(null);

  const handleAfterUpload = async (processedFilesArray: ProcessedFile[]) => {
    setUploadError(null);
    const newFiles = [
      ...watchFiles,
      ...processedFilesArray.map((processedFile) => ({
        id: processedFile.file.id,
        fileName: processedFile.file.fileName,
        fileURL: processedFile.file.fileURL,
        contentType: processedFile.mimeType,
        size: processedFile.size,
      })),
    ];
    setValue("files", newFiles);
  };

  // Handle removing a file
  const handleFileRemove = (fileToRemove) => {
    const updatedFiles = watchFiles.filter((file) => file !== fileToRemove);
    setValue("files", updatedFiles);
  };

  const truncateText = (text, maxLength) => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  // Render file list
  const renderFileList = () => {
    return watchFiles.map((file, index) => (
      <View key={index} style={styles.uploadedFile}>
        <View style={{ flexDirection: "row", alignItems: "center" }}>
          <File />
          <CustomSpacing type="horizontal" size="s" />
          <CustomText size="m" weight="ultraBold" text={truncateText(file.fileName, 40)} useTranslationText={false} />
        </View>

        <CustomButton
          leftIcon={<Remove style={{ paddingRight: 4 }} />}
          text="Remove"
          size="s"
          style={{ backgroundColor: "#D0D5DD", borderRadius: 4 }}
          textStyle={{ fontSize: 14, fontWeight: 700 }}
          onPress={() => handleFileRemove(file)}
        />
      </View>
    ));
  };

  // Helper function to check if description has meaningful content
  const hasValidDescription = (description) => {
    if (!description) return false;
    // Remove HTML tags and check if there's actual text content
    const textContent = description.replace(/<[^>]*>/g, "").trim();
    return textContent.length > 0;
  };

  // Form submission
  const onSubmit = (data) => {
    if (!data.activityName.trim()) {
      return;
    }

    if (!hasValidDescription(data.description)) {
      return;
    }

    const filesForSubmission = (data.files || []).map((file) => ({
      fileId: file.id || file.fileId,
      fileName: file.fileName,
      fileURL: file.fileURL,
      contentType: file.contentType,
      size: file.size,
    }));

    // Create the activity data object with correct backend schema fields
    const updatedActivityData = {
      id: activityData?.id,
      title: data.activityName,
      description: data.description,
      scopeDocument: data.scopeDocument || "",
      deliverableTypes: data.deliverableTypes || [],
      files: filesForSubmission,
      assessmentCriteria: data.assessmentCriteria
        ? data.assessmentCriteria.split("\n").filter((item) => item.trim())
        : [],
      expectedOutcomes: data.expectedOutcomes ? data.expectedOutcomes.split("\n").filter((item) => item.trim()) : [],
      dueDate: data.dueDate || "",
      type: "Project",
      activityState: data.activityState,
      scheduledDate: data.scheduledDate,
    };

    onSave(updatedActivityData);
  };

  return (
    <FormLayout
      title={`${activityData?.id ? "Edit" : "Create"} Project`}
      isSubmitting={isSubmitting}
      onCancel={onCancel}
      onSubmit={handleSubmit(onSubmit)}
      actionText={isNewActivity ? "Create" : "Save"}
      disableSubmit={!watchActivityName || !hasValidDescription(watchDescription)}
      error={error}>
      <CustomText text="Project Name" size="m" weight="ultraBold" />
      <CustomText style={styles.subText} text="Keep it short and action oriented" />
      <CustomSpacing type="vertical" size="s" />
      <Controller
        name="activityName"
        control={control}
        rules={{ required: true }}
        render={({ field: { onChange, onBlur, value } }) => (
          <div>
            <CustomTextInput
              placeholder="Enter project name"
              onBlur={onBlur}
              onChangeText={onChange}
              value={value}
              style={styles.textInput}
            />
          </div>
        )}
      />
      {errors.activityName && <CustomText text="Project name is required" style={styles.errorText} />}

      <CustomSpacing type="vertical" size="xl" />
      <CustomText text="Project Description" size="m" weight="ultraBold" />
      <CustomText style={styles.subText} text="What is this about and why it matters" />
      <CustomSpacing type="vertical" size="s" />
      <Controller
        name="description"
        control={control}
        rules={{ required: true }}
        render={({ field: { onChange, onBlur, value } }) => (
          <CustomTextInput
            placeholder="In 2-3 sentences, explain the goal, audience, and what the students will produce."
            onBlur={onBlur}
            onChangeText={onChange}
            value={value}
            multiline={true}
            numberOfLines={4}
            style={styles.textInput}
          />
        )}
      />

      {errors.description && <CustomText text="Description is required" style={styles.errorText} />}

      <CustomSpacing type="vertical" size="xl" />
      <CustomText text="Scope Document" size="m" weight="ultraBold" />
      <CustomText
        style={styles.subText}
        text="Define what students need to build, key requirements, and project boundaries"
      />
      <CustomSpacing type="vertical" size="s" />
      <Controller
        name="scopeDocument"
        control={control}
        render={({ field: { onChange, onBlur, value } }) => (
          <CustomTextInput
            placeholder="Define what students need to build, key requirements, and project boundaries..."
            onBlur={onBlur}
            onChangeText={onChange}
            value={value}
            multiline={true}
            numberOfLines={4}
            style={styles.textInput}
          />
        )}
      />

      <CustomSpacing type="vertical" size="xl" />
      <CustomText text="Deliverable Types (Optional)" size="m" weight="ultraBold" />
      <CustomSpacing type="vertical" size="s" />
      <Controller
        name="deliverableTypes"
        control={control}
        render={({ field: { onChange, onBlur, value } }) => (
          <div style={styles.deliverableOptionWrapper}>
            {deliverableTypeOptions.map((option) => (
              <div
                key={option}
                onClick={() => {
                  if (value.includes(option)) {
                    onChange(value.filter((opt) => opt !== option));
                  } else {
                    onChange([...value, option]);
                  }
                }}>
                <CustomText
                  text={option}
                  size="s"
                  style={[
                    styles.deliverableOption,
                    { textWrap: "nowrap" },
                    value.includes(option) ? styles.activeOption : {},
                  ]}
                />
              </div>
            ))}
          </div>
        )}
      />

      <CustomSpacing type="vertical" size="xl" />
      <CustomText text="Assessment Criteria (optional)" size="m" weight="ultraBold" />
      <CustomText style={styles.subText} text="Define how projects will be evaluated (one per line)" />
      <CustomSpacing type="vertical" size="s" />
      <Controller
        name="assessmentCriteria"
        control={control}
        render={({ field: { onChange, onBlur, value } }) => (
          <CustomTextInput
            placeholder="Code quality and organization (25%)&#10;Functionality and features (35%)&#10;User interface design (25%)&#10;Documentation quality (15%)"
            onBlur={onBlur}
            onChangeText={onChange}
            value={value}
            multiline={true}
            numberOfLines={5}
            style={styles.textInput}
          />
        )}
      />

      <CustomSpacing type="vertical" size="xl" />
      <CustomText text="Expected Learning Outcomes (optional)" size="m" weight="ultraBold" />
      <CustomText style={styles.subText} text="List expected learning outcomes (one per line)" />
      <CustomSpacing type="vertical" size="s" />
      <Controller
        name="expectedOutcomes"
        control={control}
        render={({ field: { onChange, onBlur, value } }) => (
          <CustomTextInput
            placeholder="Demonstrate understanding of component lifecycle&#10;Implement state management&#10;Create responsive user interfaces"
            onBlur={onBlur}
            onChangeText={onChange}
            value={value}
            multiline={true}
            numberOfLines={4}
            style={styles.textInput}
          />
        )}
      />

      <CustomSpacing type="vertical" size="xl" />
      <CustomText text="Due Date (Optional)" size="m" weight="ultraBold" />
      <CustomText style={styles.subText} text="Set a due date for project submissions" />
      <CustomSpacing type="vertical" size="s" />
      <Controller
        name="dueDate"
        control={control}
        rules={{ required: false }}
        render={({ field: { onChange, value } }) => (
          <div style={{ maxWidth: 250 }}>
            <DatePickerInput
              locale="en"
              value={value instanceof Date ? value : value ? new Date(value) : undefined}
              onChange={onChange}
              inputMode="start"
              mode="outlined"
              outlineColor={"#D1D5DB"}
              selectionColor={undefined}
              activeOutlineColor={"blue"}
              withDateFormatInLabel={false}
              placeholder="MM/DD/YYYY"
              theme={{
                colors: {
                  primary: "#000000",
                  onSurface: "#667085",
                },
                roundness: 8,
              }}
              style={styles.datePicker}
              aria-label="Due date date picker"
            />
          </div>
        )}
      />
      <CustomSpacing type="vertical" size="xl" />
      <CustomText text="Starter Resources (Optional)" size="m" weight="ultraBold" />
      <CustomText style={styles.subText} text="Upload files required for students to get started" />
      <CustomSpacing type="vertical" size="s" />
      <FileUploadComponent
        storagePath={StoragePathType.ProjectStarterResources}
        afterUpload={handleAfterUpload}
        onError={(msg) => setUploadError(msg)}
        multiple={true}
      />
      {uploadError && <CustomText useTranslationText={false} text={uploadError} style={styles.errorText} />}
      {renderFileList()}
      {watchDueDate && (
        <CustomText
          text={`Due: ${new Date(watchDueDate).toLocaleDateString(undefined, {
            year: "numeric",
            month: "long",
            day: "numeric",
          })}`}
          size="s"
          style={[styles.subText, { marginTop: 8, color: "#3E68FE" }]}
        />
      )}
      <CustomSpacing type="vertical" size="xl" />
      <View style={styles.activityState}>
        <CustomText text="Publish this Activity" size="l" weight="ultraBold" useTranslationText={false} />
        <CustomText
          text="Publish this activity now or schedule a publish date."
          size="s"
          useTranslationText={false}
          style={styles.stepDescription}
        />
        <Controller
          control={control}
          name="activityState"
          render={({ field: { onChange, value } }) => (
            <div style={{ display: "flex", gap: 10, marginTop: 5 }}>
              {Object.keys(ActivityState).map((state) => (
                <CustomButton
                  key={state}
                  text={ActivityState[state]}
                  size="s"
                  style={[styles.activityStateOption, ActivityState[state] === value && styles.activeState]}
                  onPress={() => {
                    onChange(ActivityState[state]);
                  }}
                />
              ))}
            </div>
          )}
        />
        {watchActivityState === ActivityState.Scheduled && (
          <>
            <CustomText
              text="This Activity will automatically be published after:"
              size="s"
              useTranslationText={false}
              style={styles.stepDescription}
            />
            <Controller
              control={control}
              name="scheduledDate"
              render={({ field: { onChange, value } }) => (
                <div style={{ maxWidth: 250, marginTop: 10 }}>
                  <DatePickerInput
                    locale="en"
                    value={value instanceof Date ? value : value ? new Date(value) : undefined}
                    onChange={onChange}
                    inputMode="start"
                    mode="outlined"
                    outlineColor={"#D1D5DB"}
                    selectionColor={undefined}
                    activeOutlineColor={"blue"}
                    withDateFormatInLabel={false}
                    placeholder="MM/DD/YYYY"
                    theme={{
                      colors: {
                        primary: "#000000",
                        onSurface: "#667085",
                      },
                      roundness: 8,
                    }}
                    style={styles.datePicker}
                    aria-label="Schedule published date picker"
                  />
                </div>
              )}
            />
          </>
        )}
      </View>
    </FormLayout>
  );
};

const styles = StyleSheet.create({
  subText: {
    color: "#667085",
    fontSize: 14,
    fontWeight: "400",
  },
  errorText: {
    color: "#FF0000",
    marginTop: 8,
  },
  datePicker: {
    backgroundColor: "#FFFFFF",
    borderColor: "#D5D7DA",
    borderRadius: 10,
    color: "#000000",
    fontSize: 14,
  },
  textInput: {
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: "#FFFFFF",
    minHeight: undefined,
  },
  uploadedFile: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 12,
    backgroundColor: "#F3F4F6",
    borderRadius: 8,
    marginBottom: 16,
    marginTop: 16,
  },
  deliverableOption: {
    height: 28,
    borderRadius: 100,
    paddingVertical: 4,
    paddingHorizontal: 8,
    backgroundColor: "#EAECF0",
    cursor: "pointer",
    borderStyle: "solid",
    borderWidth: 2,
    borderColor: "transparent",
  },
  activeOption: {
    backgroundColor: "#0084FF1A",
    borderStyle: "solid",
    borderWidth: 2,
    borderColor: "#0084FF",
  },
  deliverableOptionWrapper: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    flexShrink: 0,
    gap: 10,
  },
  activityState: {
    marginBottom: 20,
    padding: 15,
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
  },
  activeState: {
    backgroundColor: "#0084FF1A",
    color: "white",
    borderColor: "#0084FF",
  },
  activityStateOption: {
    borderRadius: 30,
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderWidth: 2,
    borderStyle: "solid",
    color: "#667085",
    borderColor: "transparent",
  },
  stepDescription: {
    color: "#667085",
    marginTop: 5,
  },
});

export default ProjectForm;
